import { get } from 'lodash'
import React from 'react'
import styled from 'styled-components'

// @ts-expect-error: this version of @gnomad/ui has no types
import { BaseTable, ExternalLink, ListItem } from '@gnomad/ui'

import { VariantAttribute, VariantAttributeList } from './VariantAttributes'
import { renderExponentialIfSmall, VariantRow } from './variantTableColumns'
import {
  GeneResultColumnGroup,
  ReferenceGenome,
  RenderVariantAttributes,
  VariantColumnConfig,
} from '../Browser'
import { FilterState } from './VariantFilterControls'

const VariantContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 300px;
`

const Column = styled.div``

const Columns = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  margin: 1em 0;

  ${Column} {
    flex-basis: calc(50% - 1em);
  }

  @media (max-width: 600px) {
    ${Column} {
      flex-basis: 100%;
    }
  }
`

const VariantSummaryGroup = styled.div`
  margin-bottom: 1em;

  > dt {
    font-weight: bold;
  }

  > dd {
    margin-left: 1em;
  }

  > dd > dl {
    margin: 0.5em 0 0;
  }
`

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  th,
  td {
    white-space: nowrap;
  }
`

const CSQ_CODING_HIGH_IMPACT = [
  'transcript_ablation',
  'splice_acceptor_variant',
  'splice_donor_variant',
  'stop_gained',
  'frameshift_variant',
  'stop_lost',
]

const CSQ_CODING_MEDIUM_IMPACT = [
  'start_lost',
  'initiator_codon_variant',
  'transcript_amplification',
  'inframe_insertion',
  'inframe_deletion',
  'missense_variant',
  'protein_altering_variant',
]

const CSQ_CODING_LOW_IMPACT = [
  'splice_donor_5th_base_variant',
  'splice_region_variant',
  'splice_donor_region_variant',
  'splice_polypyrimidine_tract_variant',
  'incomplete_terminal_codon_variant',
  'start_retained_variant',
  'stop_retained_variant',
  'synonymous_variant',
  'coding_sequence_variant',
  'coding_transcript_variant',
]

const CSQ_NON_CODING = [
  'mature_miRNA_variant',
  '5_prime_UTR_variant',
  '3_prime_UTR_variant',
  'non_coding_transcript_exon_variant',
  'non_coding_exon_variant',
  'intron_variant',
  'NMD_transcript_variant',
  'non_coding_transcript_variant',
  'nc_transcript_variant',
  'upstream_gene_variant',
  'downstream_gene_variant',
  'TFBS_ablation',
  'TFBS_amplification',
  'TF_binding_site_variant',
  'regulatory_region_ablation',
  'regulatory_region_amplification',
  'feature_elongation',
  'regulatory_region_variant',
  'feature_truncation',
  'intergenic_variant',
  'sequence_variant',
]

const CSQ_ORDER = [
  ...CSQ_CODING_HIGH_IMPACT,
  ...CSQ_CODING_MEDIUM_IMPACT,
  ...CSQ_CODING_LOW_IMPACT,
  ...CSQ_NON_CODING,
]

const findMostSevereConsequence = (consequences: string[]) => {
  return consequences.reduce((mostSevere, current) => {
    const currentIndex = CSQ_ORDER.indexOf(current)
    const mostSevereIndex = CSQ_ORDER.indexOf(mostSevere)
    return currentIndex < mostSevereIndex ? current : mostSevere
  })
}

type NumberBool = 0 | 1

interface RawTranscriptConsequence {
  gene_id: string
  gene_symbol: string
  transcript_id: string
  canonical: NumberBool
  mane_select: NumberBool
  hgvsp: string
  hgvsc: string
  hgnc_id: string
  domains: string
  consequence_terms: string[]
  sift_prediction: string
  polyphen_prediction: string
}

interface ParsedTranscript {
  geneId: string
  geneSymbol: string
  transcriptId: string
  isCanonical: boolean
  isManeSelect: boolean
  hgvsp: string
  hgvsc: string
  hgncID: string
  domains: string
  majorConsequence: string
  siftPrediction: string
  polyphenPrediction: string
}

type NestedParsedTranscript = Omit<ParsedTranscript, 'geneId' | 'geneSymbol' | 'majorConsequence'>

type GroupedConsequenceMap = Record<
  string,
  {
    consequence: string
    genes: Record<
      string,
      {
        geneId: string
        geneSymbol: string
        // transcripts: Omit<ParsedTranscript, 'geneId' | 'geneSymbol' | 'majorConsequence'>[],
        // transcripts: ParsedTranscript[],
        transcripts: NestedParsedTranscript[]
      }
    >
  }
>

const parseTranscriptConsequences = (transcriptConsequences: RawTranscriptConsequence[]) => {
  const reshaped: ParsedTranscript[] = transcriptConsequences.map((tc) => ({
    geneId: tc.gene_id,
    geneSymbol: tc.gene_symbol,
    transcriptId: tc.transcript_id,
    isCanonical: tc.canonical === 1,
    isManeSelect: tc.mane_select === 1,
    hgvsp: tc.hgvsp && tc.hgvsp.indexOf(':') !== -1 ? tc.hgvsp.split(':')[1] : tc.hgvsp,
    hgvsc: tc.hgvsc && tc.hgvsc.indexOf(':') !== -1 ? tc.hgvsc.split(':')[1] : tc.hgvsc,
    hgncID: tc.hgnc_id,
    domains: tc.domains,
    majorConsequence: findMostSevereConsequence(tc.consequence_terms),
    siftPrediction: tc.sift_prediction,
    polyphenPrediction: tc.polyphen_prediction,
  }))

  const groupedByConsequence = reshaped.reduce((acc: GroupedConsequenceMap, transcript) => {
    const consequence = transcript.majorConsequence

    if (!acc[consequence]) {
      acc[consequence] = {
        consequence,
        genes: {},
      }
    }

    const geneKey = `${transcript.geneId}-${transcript.geneSymbol}`

    if (!acc[consequence].genes[geneKey]) {
      acc[consequence].genes[geneKey] = {
        geneId: transcript.geneId,
        geneSymbol: transcript.geneSymbol ? transcript.geneSymbol : transcript.geneId,
        transcripts: [],
      }
    }

    const { geneId, geneSymbol, majorConsequence: _, ...transcriptData } = transcript
    acc[consequence].genes[geneKey].transcripts.push(transcriptData)

    return acc
  }, {})

  const result = Object.values(groupedByConsequence)
    .map((consequenceGroup) => ({
      consequence: consequenceGroup.consequence,
      genes: Object.values(consequenceGroup.genes),
    }))
    .sort((a, b) => CSQ_ORDER.indexOf(a.consequence) - CSQ_ORDER.indexOf(b.consequence))

  return result
}

const TranscriptConsequencesWrapper = styled.div`
  margin-top: 2em;
`

const ConsequenceListWrapper = styled.ol`
  display: flex;
  flex-flow: row wrap;
  padding: 0;
  list-style-type: none;
  margin-bottom: 1em;

  h3,
  h4 {
    margin: 0 0 0.5em;
  }
`

const ConsequenceListItem = styled.li`
  margin-right: 2em;
`

const GeneList = styled.ol`
  margin-bottom: 1em;
`

const TranscriptList = styled.ol`
  margin-bottom: 1em;
`

const TranscriptDetails = ({ transcript }: { transcript: NestedParsedTranscript }) => {
  return (
    <ListItem>
      <div>
        {
          <ExternalLink
            href={`https://gnomad.broadinstitute.org/transcript/${transcript.transcriptId}`}
          >
            {transcript.transcriptId}
          </ExternalLink>
        }
        {transcript.isManeSelect && <div>Mane Select transcript</div>}
        {transcript.isCanonical && <div>Canonical transcript</div>}
        {transcript.hgvsp && <div>{`HGVSp: ${transcript.hgvsp}`}</div>}
        {transcript.hgvsc && <div>{`HGVSc: ${transcript.hgvsc}`}</div>}
      </div>
    </ListItem>
  )
}

const TranscriptConsequences = ({
  transcriptConsequencesString,
}: {
  transcriptConsequencesString: string
}) => {
  const transcriptConsequences: RawTranscriptConsequence[] = JSON.parse(
    transcriptConsequencesString
  ).filter((tc: RawTranscriptConsequence) => tc.gene_id.startsWith('ENSG'))
  const numTranscripts = transcriptConsequences.length
  const numGenes = Array.from(new Set(transcriptConsequences.map((csq) => csq.gene_id))).length

  const parsedTranscriptConsequences = parseTranscriptConsequences(transcriptConsequences)

  return (
    <TranscriptConsequencesWrapper>
      <h2>Transcript Consequences</h2>
      <p>
        This variant falls on {numTranscripts} transcript{numTranscripts !== 1 && 's'} in {numGenes}{' '}
        gene{numGenes !== 1 && 's'}.
      </p>
      <br />

      <ConsequenceListWrapper>
        {parsedTranscriptConsequences.map((consequence) => {
          return (
            <ConsequenceListItem key={consequence.consequence}>
              <h4>{consequence.consequence}</h4>
              <GeneList>
                {consequence.genes.map((gene) => {
                  return (
                    <ListItem key={gene.geneId}>
                      <h4>
                        <ExternalLink
                          href={`https://gnomad.broadinstitute.org/gene/${gene.geneId}`}
                        >
                          {gene.geneSymbol}
                        </ExternalLink>
                      </h4>
                      <TranscriptList>
                        {gene.transcripts
                          .sort((transcript) => (transcript.isManeSelect ? 0 : 1))
                          .map((transcript) => (
                            <TranscriptDetails
                              key={transcript.transcriptId}
                              transcript={transcript}
                            />
                          ))}
                      </TranscriptList>
                    </ListItem>
                  )
                })}
              </GeneList>
            </ConsequenceListItem>
          )
        })}
      </ConsequenceListWrapper>
    </TranscriptConsequencesWrapper>
  )
}

const renderNumber = (num: number | null, precision = 3) =>
  num === null ? '–' : Number(num.toPrecision(precision)).toString()

const renderExponential = (num: number | null | undefined, precision = 3): string => {
  if (num === null || num === undefined) {
    return '-'
  }

  if (num === 0) {
    return '0'
  }

  const truncated = Number(num.toPrecision(precision))

  return truncated.toExponential(2)
}

interface VariantDetailsProps {
  datasetId: string
  defaultVariantAnalysisGroup: string
  referenceGenome: ReferenceGenome
  variant: VariantRow
  variantAnalysisGroupOptions: readonly string[]
  variantAnalysisGroupLabels: { [key: string]: string }
  variantResultColumns: VariantColumnConfig[]
  renderVariantAttributes: RenderVariantAttributes
  additionalVariantDetailSummaryColumns: VariantColumnConfig[] | undefined
  variantDetailColumns: VariantColumnConfig[] | undefined
  variantDetailsSingleAnalysisGroup?: boolean
  renderVariantTranscriptConsequences: boolean
  filter: FilterState
}

const VariantDetails = ({
  datasetId,
  defaultVariantAnalysisGroup,
  referenceGenome,
  variant: inputVariant,
  variantAnalysisGroupOptions,
  variantAnalysisGroupLabels,
  variantResultColumns,
  renderVariantAttributes,
  additionalVariantDetailSummaryColumns,
  variantDetailColumns,
  variantDetailsSingleAnalysisGroup = false,
  renderVariantTranscriptConsequences,
  filter,
}: VariantDetailsProps) => {
  const defaultGroupResult = inputVariant.group_results[defaultVariantAnalysisGroup]
  // Select default analysis group so that column render methods work correctly
  const variant = { ...inputVariant, group_result: defaultGroupResult }

  const gnomadDataset = referenceGenome === 'GRCh38' ? 'gnomad_r4' : 'gnomad_r2_1'

  const renderedVariantSummaryRows = [
    ...variantResultColumns,
    ...(additionalVariantDetailSummaryColumns || []),
  ]

  const variantSummarySections: {
    group?: GeneResultColumnGroup
    columns: VariantColumnConfig[]
  }[] = []

  renderedVariantSummaryRows.forEach((column) => {
    const group = datasetId === 'AGB' ? column.group : undefined
    const existingSection = group
      ? variantSummarySections.find((section) => section.group?.key === group.key)
      : undefined

    if (existingSection) {
      existingSection.columns.push(column)
    } else {
      variantSummarySections.push({ group, columns: [column] })
    }
  })

  const standardColumns: VariantColumnConfig[] = [
    { key: 'group_result.ac_case', heading: 'AC Case', render: (value: number) => value },
    { key: 'group_result.an_case', heading: 'AN Case', render: (value: number) => value },
    { key: 'group_result.ac_ctrl', heading: 'AC Ctrl', render: (value: number) => value },
    { key: 'group_result.an_ctrl', heading: 'AN Ctrl', render: (value: number) => value },
    {
      key: 'group_result.af_case',
      heading: 'AF Case',
      render: (value: number) => renderExponential(value),
    },
    {
      key: 'group_result.af_ctrl',
      heading: 'AF Ctrl',
      render: (value: number) => renderExponential(value),
    },
  ]

  const gp2Columns: VariantColumnConfig[] = [
    // ---
    {
      key: 'group_result.wgs_ac_pd',
      heading: 'WGS AC PD',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_an_pd',
      heading: 'WGS AN PD',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_af_pd',
      heading: 'WGS AF PD',
      render: (value) => renderExponentialIfSmall(value),
    },
    // ---
    {
      key: 'group_result.wgs_ac_psp',
      heading: 'WGS AC PSP',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_an_psp',
      heading: 'WGS AN PSP',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_af_psp',
      heading: 'WGS AF PSP',
      render: (value) => renderExponentialIfSmall(value),
    },
    // ---
    {
      key: 'group_result.wgs_ac_dlb',
      heading: 'WGS AC DLB',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_an_dlb',
      heading: 'WGS AN DLB',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_af_dlb',
      heading: 'WGS AF DLB',
      render: (value) => renderExponentialIfSmall(value),
    },
    // ---
    {
      key: 'group_result.wgs_ac_msa',
      heading: 'WGS AC MSA',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_an_msa',
      heading: 'WGS AN MSA',
      render: (value: number) => value,
    },
    {
      key: 'group_result.wgs_af_msa',
      heading: 'WGS AF MSA',
      render: (value) => renderExponentialIfSmall(value),
    },
    // ---
    {
      key: 'group_result.wgs_ac_ctrl',
      heading: 'WGS AC Control',
      render: (value) => value,
    },
    {
      key: 'group_result.wgs_an_ctrl',
      heading: 'WGS AN Control',
      render: (value) => value,
    },
    {
      key: 'group_result.wgs_af_ctrl',
      heading: 'WGS AF Control',
      render: renderExponentialIfSmall,
    },
    // ---
    {
      key: 'group_result.wgs_ac_other',
      heading: 'WGS AC Other',
      render: (value) => value,
    },
    {
      key: 'group_result.wgs_an_other',
      heading: 'WGS AN Other',
      render: (value) => value,
    },
    {
      key: 'group_result.wgs_af_other',
      heading: 'WGS AF Other',
      render: renderExponentialIfSmall,
    },
    // ---
    {
      key: 'group_result.ces_ac_pd',
      heading: 'CES AC PD',
      render: (value: number) => value,
    },
    {
      key: 'group_result.ces_an_pd',
      heading: 'CES AN PD',
      render: (value: number) => value,
    },
    {
      key: 'group_result.ces_af_pd',
      heading: 'CES AF PD',
      render: (value) => renderExponentialIfSmall(value),
    },
  ]

  const allGP2CaseColumnGroups = Object.keys(filter.gp2VariantColumnGroups || [])
  const activeGP2CaseColumnGroups = allGP2CaseColumnGroups.filter(
    (g) => filter.gp2VariantColumnGroups![g]
  )
  const renderedGP2Columns = gp2Columns.filter((col) => {
    const suffix = col.key.split('_').pop() as string

    if (allGP2CaseColumnGroups.includes(suffix)) {
      return activeGP2CaseColumnGroups.includes(suffix)
    }

    return true
  })

  const datasetColumns =
    datasetId === 'GP2' ? renderedGP2Columns : datasetId === 'AGB' ? [] : standardColumns

  const filteredVariantResultColumns =
    datasetId === 'AGB' && filter.agbVariantColumnGroups
      ? variantResultColumns.filter(
          (column) => !column.group || filter.agbVariantColumnGroups![column.group.key]
        )
      : variantResultColumns

  const renderedVariantColumns = variantDetailColumns || [
    ...datasetColumns,
    ...filteredVariantResultColumns,
  ]

  if (datasetId === 'GP2') {
    const parsedGroupResults = variantAnalysisGroupOptions.reduce(
      (acc: Record<string, any>, group) => {
        acc[group] = variant.group_results[group] || {}
        return acc
      },
      {}
    )

    variant.group_results = parsedGroupResults
  }

  const analysisLabel = variantDetailsSingleAnalysisGroup
    ? 'Analysis'
    : `Analysis (${defaultVariantAnalysisGroup})`

  return (
    <VariantContainer>
      <div>
        <ExternalLink
          href={`https://gnomad.broadinstitute.org/variant/${variant.variant_id}?dataset=${gnomadDataset}`}
        >
          View in gnomAD
        </ExternalLink>{' '}
        for more information on variant consequences and population frequencies
      </div>
      <Columns>
        {datasetId !== 'GP2' && defaultGroupResult && (
          <Column>
            <VariantAttributeList label={analysisLabel}>
              {datasetId !== 'AGB' && (
                <>
                  <VariantAttribute label="Cases">
                    {defaultGroupResult.ac_case} / {defaultGroupResult.an_case} (
                    {renderExponential(defaultGroupResult.af_case, 4)})
                  </VariantAttribute>
                  <VariantAttribute label="Controls">
                    {defaultGroupResult.ac_ctrl} / {defaultGroupResult.an_ctrl} (
                    {renderExponential(defaultGroupResult.af_ctrl, 4)})
                  </VariantAttribute>
                </>
              )}
              {variantSummarySections.map(({ group, columns }) => {
                const attributes = columns.map((column) => (
                  <VariantAttribute key={column.key} label={column.heading || ''}>
                    {get(variant, column.key) === null
                      ? null
                      : (column.render || renderNumber)(get(variant, column.key))}
                  </VariantAttribute>
                ))

                if (!group) {
                  return attributes
                }

                return (
                  <VariantSummaryGroup key={group.key}>
                    <dt>{group.variantTableLabel}</dt>
                    <dd>
                      <dl>{attributes}</dl>
                    </dd>
                  </VariantSummaryGroup>
                )
              })}
            </VariantAttributeList>
          </Column>
        )}

        <Column>
          <VariantAttributeList label="Annotations">
            <VariantAttribute label="HGVSc">{variant.hgvsc}</VariantAttribute>
            <VariantAttribute label="HGVSp">{variant.hgvsp}</VariantAttribute>
            <VariantAttribute label="Consequence">{variant.consequence}</VariantAttribute>
            {renderVariantAttributes &&
              renderVariantAttributes(variant.info).map(
                (
                  { label, content }: { label: React.ReactNode; content: React.ReactNode },
                  index
                ) => (
                  <VariantAttribute key={index} label={label}>
                    {content}
                  </VariantAttribute>
                )
              )}
          </VariantAttributeList>
        </Column>
      </Columns>

      {!variantDetailsSingleAnalysisGroup && (
        <>
          <h2>Analysis Groups</h2>
          <TableWrapper>
            <BaseTable>
              <thead>
                <tr>
                  <th scope="col">Group</th>
                  {renderedVariantColumns.map((c) => (
                    <th key={c.key} scope="col">
                      {c.heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(variant.group_results)
                  .sort((g1, g2) => {
                    if (g1 === defaultVariantAnalysisGroup) {
                      return -1
                    }
                    if (g2 === defaultVariantAnalysisGroup) {
                      return 1
                    }
                    return (variantAnalysisGroupLabels[g1] || g1).localeCompare(
                      variantAnalysisGroupLabels[g2] || g2
                    )
                  })
                  .map((analysisGroup) => {
                    const groupResult = variant.group_results[analysisGroup]
                    const rowVariant = { ...variant, group_result: groupResult }

                    return (
                      <tr key={analysisGroup}>
                        <th scope="row">
                          {variantAnalysisGroupLabels[analysisGroup] || analysisGroup}
                        </th>
                        {renderedVariantColumns.map((c) => (
                          <td key={c.key}>
                            {get(rowVariant, c.key) === null
                              ? ''
                              : (c.render || renderNumber)(get(rowVariant, c.key))}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
              </tbody>
            </BaseTable>
          </TableWrapper>
        </>
      )}

      {renderVariantTranscriptConsequences && variant.info && variant.info.transcript_consequences && (
        <>
          <TranscriptConsequences
            transcriptConsequencesString={variant.info.transcript_consequences}
          />
        </>
      )}
    </VariantContainer>
  )
}

export default VariantDetails
